import React, { useEffect, useState } from 'react';
import { API_BASE, apiFetch } from './api';
import { theme } from './theme';

const NUMERIC_LABELS = {
  users: 'User',
  drivers: 'Fahrer',
  vehicles: 'Fahrzeuge',
  vehicleClasses: 'Fahrzeugklassen',
  gates: 'Tore',
  loadingBays: 'Ladezonen',
};

const FEATURE_GROUPS = [
  {
    title: 'Dispo',
    items: [
      { key: 'gateManagement', label: 'Torverwaltung' },
      { key: 'loadingBayManagement', label: 'Ladezonenverwaltung' },
    ],
  },
  {
    title: 'Layout',
    items: [
      { key: 'warehouseLayout', label: 'Lagerlayout' },
      { key: 'warehouseLookup', label: 'Lager Lookup' },
      { key: 'layoutDownload', label: 'Layout Download' },
      { key: 'layoutUpload', label: 'Layout Upload' },
    ],
  },
  {
    title: 'Integrationen und Design',
    items: [
      { key: 'rentmanApi', label: 'Rentman API' },
      { key: 'customLogo', label: 'Custom Logo' },
      { key: 'customDomain', label: 'Custom Domain' },
      { key: 'customColors', label: 'Custom Farben' },
    ],
  },
];

const styles = {
  stack: { display: 'grid', gap: '20px', width: '100%', maxWidth: '100%', minWidth: 0 },
  hero: { ...theme.glassCard, padding: '24px', color: 'var(--board-text-strong, #fff)' },
  eyebrow: { margin: '0 0 8px', color: 'var(--board-text-muted, rgba(255,255,255,0.56))', fontSize: '12px', fontWeight: 800, textTransform: 'uppercase' },
  title: { margin: 0, fontSize: '28px', color: 'var(--board-text-strong, #fff)' },
  subtitle: { margin: '10px 0 0', color: 'var(--board-text-soft, rgba(255,255,255,0.72))', fontSize: '14px' },
  numericGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px', width: '100%' },
  card: { ...theme.glassCard, padding: '18px', color: 'var(--board-text-strong, #fff)', width: '100%', maxWidth: '100%', minWidth: 0 },
  cardTitle: { margin: 0, fontSize: '18px', color: 'var(--board-text-strong, #fff)' },
  value: { margin: '8px 0 14px', color: 'var(--board-text-soft, rgba(255,255,255,0.78))', fontSize: '14px' },
  barTrack: { width: '100%', height: '12px', borderRadius: '999px', background: 'var(--board-surface-soft, rgba(255,255,255,0.12))', overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: '999px', transition: 'width 220ms ease-out, background-color 220ms ease-out' },
  groupCard: { ...theme.glassCard, padding: '20px', color: 'var(--board-text-strong, #fff)', width: '100%', maxWidth: '100%', minWidth: 0 },
  groupTitle: { margin: '0 0 14px', fontSize: '18px', color: 'var(--board-text-strong, #fff)' },
  featureList: { display: 'grid', gap: '10px' },
  featureItem: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', borderRadius: '6px', background: 'var(--board-surface-soft, rgba(255,255,255,0.06))', border: '1px solid var(--board-border, rgba(255,255,255,0.12))' },
  check: { width: '26px', height: '26px', borderRadius: '999px', display: 'grid', placeItems: 'center', fontSize: '15px', fontWeight: 900, flex: '0 0 auto' },
  featureLabel: { color: 'var(--board-text-strong, #fff)', fontSize: '14px', fontWeight: 700 },
  featureText: { display: 'grid', gap: '3px' },
  featureStatus: { color: 'var(--board-text-muted, rgba(255,255,255,0.56))', fontSize: '12px' },
  loading: { color: 'var(--board-text-muted, rgba(255,255,255,0.62))' },
};

function getUsageColor(item) {
  if (item.key !== 'users' && !item.enabled) return '#52525b';
  if (!item.enabled || !item.max) return '#ffffff';
  if (item.current > item.max) return '#ef4444';
  if (item.current >= item.max) return '#fda4af';
  if (item.current / item.max >= 0.8) return '#facc15';
  return '#84cc16';
}

function getUsageWidth(item) {
  if (item.key !== 'users' && !item.enabled) return '100%';
  if (!item.enabled || !item.max) return '100%';
  return `${Math.max(6, Math.min(100, (item.current / item.max) * 100))}%`;
}

function numericTitle(item) {
  const label = NUMERIC_LABELS[item.key] || item.key;
  if (item.key !== 'users' && !item.enabled) return `${label} (nicht freigeschaltet)`;
  if (!item.max) return `${label} (${item.current}/unbegrenzt)`;
  return `${label} (${item.current}/${item.max})`;
}

function numericText(item) {
  if (item.key !== 'users' && !item.enabled) return 'Diese Funktion ist in deiner Lizenz nicht enthalten.';
  if (!item.max) return 'Unbegrenzt nutzbar.';
  return `Aktive Nutzung von ${item.max}`;
}

function LicenseView() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch(`${API_BASE}/license`)
      .then(res => res.json())
      .then(payload => setData(payload))
      .catch(err => setError(err.message));
  }, []);

  const featureMap = data?.features
    ? Object.fromEntries(data.features.map(item => [item.key, item]))
    : {};

  return (
    <div style={styles.stack}>
      <section style={styles.hero}>
        <p style={styles.eyebrow}>Lizenz</p>
        <h1 style={styles.title}>Deine Lizenz: {data?.licensePresetName || '-'}</h1>
        <p style={styles.subtitle}>Hier siehst du aktuelle Limits, Auslastung und freigeschaltete Funktionen dieser Instanz.</p>
      </section>

      {error && <div style={styles.loading}>{error}</div>}
      {!data && !error && <div style={styles.loading}>Lizenzdaten werden geladen...</div>}

      {data && (
        <>
          <section style={styles.numericGrid}>
            {data.numeric.map(item => (
              <article key={item.key} style={styles.card}>
                <h2 style={styles.cardTitle}>
                  {numericTitle(item)}
                </h2>
                <p style={styles.value}>
                  {numericText(item)}
                </p>
                <div style={styles.barTrack}>
                  <div
                    style={{
                      ...styles.barFill,
                      width: getUsageWidth(item),
                      background: getUsageColor(item),
                    }}
                  />
                </div>
              </article>
            ))}
          </section>

          {FEATURE_GROUPS.map(group => (
            <section key={group.title} style={styles.groupCard}>
              <h2 style={styles.groupTitle}>{group.title}</h2>
              <div style={styles.featureList}>
                {group.items.map(item => {
                  const enabled = featureMap[item.key]?.enabled !== false;
                  return (
                    <div key={item.key} style={styles.featureItem}>
                      <span
                        style={{
                          ...styles.check,
                          background: enabled ? 'rgba(132,204,22,0.18)' : 'rgba(255,255,255,0.08)',
                          border: `1px solid ${enabled ? 'rgba(132,204,22,0.42)' : 'rgba(255,255,255,0.14)'}`,
                          color: enabled ? 'var(--board-success-text, #bef264)' : 'var(--board-text-faint, #a1a1aa)',
                        }}
                      >
                        {enabled ? '✓' : '•'}
                      </span>
                      <span style={styles.featureText}>
                        <span style={styles.featureLabel}>{item.label}</span>
                        <span style={styles.featureStatus}>{enabled ? 'Freigeschaltet' : 'Nicht in deiner Lizenz enthalten'}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </>
      )}
    </div>
  );
}

export default LicenseView;
